import { useCallback, useEffect, useRef, useState } from 'react';
import { Controller, type DefaultValues, type SubmitHandler, type UseFormSetError, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { z } from 'zod';
import { dealerSchema, type DealerFormInput } from '../schemas/dealer.schema';
import { Form, Input, Button, Select, Label, Checkbox } from '@/shared/components';
import { DEALER_TYPE_OPTIONS, type Dealer, type DealerType } from '../types/dealers.types';
import { useDealerStatusesQuery } from '../hooks/useDealerStatusesQuery';
import { AllowedParentSelect } from './AllowedParentSelect';
import { parentIsRequired } from '../utils/dealerParentageRules';
import { AddressLookup, type NormalisedAddress } from '@/features/address-lookup';

type DealerFormRaw = z.input<typeof dealerSchema>;

interface DealerFormProps {
  initialValues?: Partial<Dealer>;
  onSubmit: SubmitHandler<DealerFormInput>;
  isLoading?: boolean;
  submitLabel?: string;
  onSetErrorRef?: (setError: UseFormSetError<DealerFormRaw>) => void;
  mode?: 'create' | 'edit';
  canCreateSecurityGroup?: boolean;
}

export function DealerForm({
  initialValues,
  onSubmit,
  isLoading = false,
  submitLabel = 'Save',
  onSetErrorRef,
  mode = initialValues ? 'edit' : 'create',
  canCreateSecurityGroup = false,
}: DealerFormProps) {
  const { data: catalogStatuses = [] } = useDealerStatusesQuery();

  const currentStatusKey = initialValues?.status_key ?? 'active';

  // If the dealer's current status is retired, preserve it as an option so the
  // form doesn't silently drop it on submit. It will not appear in the catalog.
  const retiredOption = currentStatusKey && !catalogStatuses.find(s => s.key === currentStatusKey)
    ? [{ value: currentStatusKey, label: `${currentStatusKey} (retired)` }]
    : [];

  const statusOptions = [
    ...catalogStatuses.map(s => ({ value: s.key, label: s.label })),
    ...retiredOption,
  ];

  const { handleSubmit, control, setError, watch, setValue, formState: { isSubmitting, errors } } = useForm<DealerFormRaw, unknown, DealerFormInput>({
    resolver: zodResolver(dealerSchema),
    defaultValues: {
      name: initialValues?.name ?? '',
      trading_name: initialValues?.trading_name ?? '',
      email: initialValues?.email ?? '',
      phone: initialValues?.phone ?? '',
      website: initialValues?.website ?? '',
      franchise_code: initialValues?.franchise_code ?? '',
      status: currentStatusKey,
      type: (initialValues?.type as DealerFormInput['type']) ?? 'dealer',
      parent_dealer_id: initialValues?.parent_dealer_id ?? '',
      address_line_1: initialValues?.address_line_1 ?? '',
      address_line_2: initialValues?.address_line_2 ?? '',
      city: initialValues?.city ?? '',
      postcode: initialValues?.postcode ?? '',
      latitude: initialValues?.latitude !== undefined && initialValues?.latitude !== null
        ? Number(initialValues.latitude)
        : undefined,
      longitude: initialValues?.longitude !== undefined && initialValues?.longitude !== null
        ? Number(initialValues.longitude)
        : undefined,
      ...(mode === 'create' ? { create_security_group: false } : {}),
    } as DefaultValues<DealerFormRaw>,
  });

  useEffect(() => {
    onSetErrorRef?.(setError);
  }, [onSetErrorRef, setError]);

  const watchedType = watch('type') as DealerType;
  // Skip the reset-on-type-change on the initial mount so a valid preset
  // parent (edit mode) is preserved when the form first renders.
  const mountedTypeRef = useRef(watchedType);
  useEffect(() => {
    if (mountedTypeRef.current === watchedType) return;
    mountedTypeRef.current = watchedType;
    setValue('parent_dealer_id', '');
  }, [watchedType, setValue]);

  const [isRetrievingAddress, setIsRetrievingAddress] = useState(false);

  const handleAddressSelected = useCallback(
    (address: NormalisedAddress) => {
      setValue('address_line_1', address.addressLine1, { shouldValidate: true, shouldDirty: true });
      setValue('address_line_2', address.addressLine2, { shouldValidate: true, shouldDirty: true });
      setValue('city', address.city, { shouldValidate: true, shouldDirty: true });
      setValue('postcode', address.postcode, { shouldValidate: true, shouldDirty: true });
    },
    [setValue],
  );

  const hasChildDealers = (initialValues?.child_dealers_count ?? 0) > 0;

  return (
    <Form className="space-y-4 max-w-2xl" onSubmit={handleSubmit(onSubmit)}>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">

        {mode === 'edit' && (
          <div>
            <Label htmlFor="dealer_code">Dealer Code</Label>
            <Input
              id="dealer_code"
              defaultValue={initialValues?.dealer_code ?? ''}
              readOnly
              hint="Auto-generated at creation. Contact support to change."
            />
          </div>
        )}

        <div>
          <Label htmlFor="name" required>Name</Label>
          <Controller
            name="name"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Input id="name" placeholder="Legal name" value={field.value} onChange={field.onChange} onBlur={field.onBlur} error={!!error} hint={error?.message} />
            )}
          />
        </div>

        <div>
          <Label htmlFor="trading_name">Trading Name</Label>
          <Controller
            name="trading_name"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Input id="trading_name" placeholder="Trading name if different" value={field.value ?? ''} onChange={field.onChange} onBlur={field.onBlur} error={!!error} hint={error?.message} />
            )}
          />
        </div>

        <div>
          <Label htmlFor="status" required>Status</Label>
          <Controller
            name="status"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Select
                id="status"
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                error={!!error}
                hint={error?.message}
                options={statusOptions}
              />
            )}
          />
        </div>

        <div>
          <Label htmlFor="type" required>Type</Label>
          <Controller
            name="type"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Select
                id="type"
                placeholder="Type"
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                disabled={hasChildDealers}
                // Persistent helper text is the accessible path for the disabled
                // reason: the shared Select is unfocusable when disabled, so a
                // hover-only Tooltip is not keyboard/screen-reader discoverable.
                ariaDescribedBy={hasChildDealers ? 'type-disabled-reason' : undefined}
                error={!!error}
                hint={error?.message}
                options={DEALER_TYPE_OPTIONS}
              />
            )}
          />
          {hasChildDealers && (
            <p id="type-disabled-reason" className="mt-1 text-xs text-mp-muted">
              Detach owned dealers before changing type.
            </p>
          )}
        </div>

        <div>
          <Label htmlFor="parent_dealer_id" required={parentIsRequired(watchedType)}>Owning Parent</Label>
          <Controller
            name="parent_dealer_id"
            control={control}
            render={({ field }) => (
              <AllowedParentSelect
                type={watchedType}
                value={field.value || undefined}
                onChange={(id) => field.onChange(id ?? '')}
                excludeId={initialValues?.id}
                required={parentIsRequired(watchedType)}
              />
            )}
          />
          {errors.parent_dealer_id?.message && (
            <p className="mt-1 text-xs text-mp-error">{errors.parent_dealer_id.message}</p>
          )}
        </div>

        {mode === 'create' && canCreateSecurityGroup && (
          <div className="md:col-span-2">
            <Controller
              name="create_security_group"
              control={control}
              shouldUnregister
              render={({ field }) => (
                <Checkbox
                  id="create_security_group"
                  label="Create security group for this dealer"
                  ariaLabel="Create security group for this dealer"
                  ariaDescribedBy="create_security_group-hint"
                  checked={field.value ?? false}
                  onChange={field.onChange}
                />
              )}
            />
            <p id="create_security_group-hint" className="mt-1 text-xs text-mp-muted">
              Creates &quot;&lt;Dealer name&gt; Security Group&quot; owned by this dealer and attaches the dealer.
              It can take a few seconds to appear on the dealer&apos;s Security Groups tab.
            </p>
          </div>
        )}

        <div className="border-t border-mp-border my-4 md:col-span-2" />

        <div className="md:col-span-2">
          <Label htmlFor="addressFind">Find address</Label>
          <AddressLookup
            id="addressFind"
            onAddressSelected={handleAddressSelected}
            onRetrievePendingChange={setIsRetrievingAddress}
          />
        </div>

        <div className="md:col-span-2">
          <Label htmlFor="address_line_1" required>Address Line 1</Label>
          <Controller
            name="address_line_1"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Input id="address_line_1" placeholder="Street address" value={field.value} onChange={field.onChange} onBlur={field.onBlur} error={!!error} hint={error?.message} />
            )}
          />
        </div>

        <div className="md:col-span-2">
          <Label htmlFor="address_line_2">Address Line 2</Label>
          <Controller
            name="address_line_2"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Input id="address_line_2" placeholder="Suite, unit, etc." value={field.value ?? ''} onChange={field.onChange} onBlur={field.onBlur} error={!!error} hint={error?.message} />
            )}
          />
        </div>

        <div>
          <Label htmlFor="city" required>City</Label>
          <Controller
            name="city"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Input id="city" placeholder="City" value={field.value} onChange={field.onChange} onBlur={field.onBlur} error={!!error} hint={error?.message} />
            )}
          />
        </div>

        <div>
          <Label htmlFor="postcode" required>Postcode</Label>
          <Controller
            name="postcode"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Input id="postcode" placeholder="SW1A 1AA" value={field.value} onChange={field.onChange} onBlur={field.onBlur} error={!!error} hint={error?.message} />
            )}
          />
        </div>

        <div className="border-t border-mp-border my-4 md:col-span-2" />

        <div>
          <Label htmlFor="latitude">Latitude</Label>
          <Controller
            name="latitude"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Input
                id="latitude"
                type="number"
                placeholder="e.g. 51.5074"
                value={field.value ?? ''}
                onChange={(e) => field.onChange(e.target.value === '' ? undefined : parseFloat(e.target.value))}
                onBlur={field.onBlur}
                error={!!error}
                hint={error?.message}
              />
            )}
          />
        </div>

        <div>
          <Label htmlFor="longitude">Longitude</Label>
          <Controller
            name="longitude"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Input
                id="longitude"
                type="number"
                placeholder="e.g. -0.1278"
                value={field.value ?? ''}
                onChange={(e) => field.onChange(e.target.value === '' ? undefined : parseFloat(e.target.value))}
                onBlur={field.onBlur}
                error={!!error}
                hint={error?.message}
              />
            )}
          />
        </div>

        <div className="border-t border-mp-border my-4 md:col-span-2" />

        <div>
          <Label htmlFor="email">Email</Label>
          <Controller
            name="email"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Input id="email" type="email" placeholder="dealer@example.com" value={field.value ?? ''} onChange={field.onChange} onBlur={field.onBlur} error={!!error} hint={error?.message} />
            )}
          />
        </div>

        <div>
          <Label htmlFor="phone">Phone</Label>
          <Controller
            name="phone"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Input id="phone" placeholder="+44 ..." value={field.value ?? ''} onChange={field.onChange} onBlur={field.onBlur} error={!!error} hint={error?.message} />
            )}
          />
        </div>

        <div className="md:col-span-2">
          <Label htmlFor="website">Website</Label>
          <Controller
            name="website"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Input id="website" placeholder="https://..." value={field.value ?? ''} onChange={field.onChange} onBlur={field.onBlur} error={!!error} hint={error?.message} />
            )}
          />
        </div>

        <div>
          <Label htmlFor="franchise_code">Franchise Code</Label>
          <Controller
            name="franchise_code"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Input id="franchise_code" placeholder="e.g. FR-2207" value={field.value ?? ''} onChange={field.onChange} onBlur={field.onBlur} error={!!error} hint={error?.message} />
            )}
          />
        </div>

      </div>

      {/* Blocked during a retrieve: in edit mode the form still holds the previous
          valid address, so an early submit would send it and drop the selection. */}
      <Button size="sm" disabled={isLoading || isSubmitting || isRetrievingAddress}>
        {isLoading || isSubmitting ? 'Saving...' : submitLabel}
      </Button>
    </Form>
  );
}
